import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Select, Input, List, DatePicker } from 'antd';
import  axios from 'axios';
import { connect } from 'react-redux';
import profileService from '../../services/profileService';
import dayjs from 'dayjs';
import { decodeJSON } from '../../utils';

const ProfileProperties = ({workingFolder, setCurrentValues, closePropDropdownOnSelect}) => {

  const [properties, setProperties] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [profileData, setProfileData] = useState([]);

  useEffect(() => {
      profileService.getProfileList(workingFolder).then(res => {
      setProfileData(res.data.data)
      setSelectedProfile(res.data.data[0].id)
    })
  }, [workingFolder]);

  useEffect(() => {

    const props =  _.map(properties, o => {

      let propValue = o.value;

      if(o.validationRule.toLowerCase() === 'batesnumber' || o.validationRule.toLowerCase() === 'autogenerated') {
        propValue = '';
      }
      
      const propType = o.type.toLowerCase();

      switch (propType) {
        case 'combo':
          propValue = o.value.join(';');
          break;
        case 'date':
          propValue = o.value ? dayjs(o.value).format('MM/DD/YYYY') : '';
        default:
          break;
      }

      return { name: o.name, value: propValue, required: o.required, validationRule: o.validationRule}
    });

    const profileData = { profileName: selectedProfile, properties: props }

    setCurrentValues(profileData)
  }, [properties])

  const getPropertyValues = (profileName, propertyInfo, values) => {
    if(propertyInfo.type.toLowerCase() === 'combo') {

      const postData = {
        'currentValues': values,
        'profileName': profileName,
        'propertyName': propertyInfo.id
      }

      return axios.post('/api/wt/profiles/properties/values/read', postData);
    }
  }

  const updateChildProperties = async (props, parentId, selectedProfile) => {

    // find direct children of the given parentId
    const children = _.filter(props, (o) => _.includes(o.parents, parentId));
  
    if (_.isEmpty(children)) {
      return props; // no children to update
    }
  
    // process all children asynchronously
    await Promise.all(children.map(async (child) => {
      const currentValues = _.map(props, (o) => ({ id: o.id, value: o.value }));
  
      // fetch new options for the child
      const res = await getPropertyValues(selectedProfile, child, currentValues);
  
      const childIndex = _.findIndex(props, (o) => o.id === child.id);
      if (childIndex !== -1) {
        const updatedChild = { ...props[childIndex] };
        const options = res.data.data;
        updatedChild.options = options;
        updatedChild.value = [];
        updatedChild.open = false;
  
        if (options.length === 1) {
          updatedChild.value = [options[0].name];
        }
  
        props[childIndex] = updatedChild;
  
        // recursively reset the child’s children
        props = await updateChildProperties(props, child.id, selectedProfile);
      }
    }));
  
    return props;
  };
  
  
  useEffect(() => {
    setInitLoading(true);
    if(selectedProfile) {

      const populateProperties = async () => {
        const res = await profileService.getProperties(workingFolder, selectedProfile);

        let props = [...res.data.data];

        for(const p of props) {

          if(p.type.toLowerCase() === 'combo') {

            const cv = _.map(props, o => ({ id: o.id, value: o.value }));
            
            const pVal = await getPropertyValues(selectedProfile, p, cv);
            const propValues = pVal.data.data;

            p.open = false;
            p.options = decodeJSON(propValues);

            if(propValues.length === 1 && p.value.length === 0) {
              const singleValue = propValues[0];
              p.value = [singleValue.name];
            }
          }

          if(p.type.toLowerCase() === 'date') {
            if(p.value.length > 0) {
              p.value = dayjs(p.value.replace(/\b(\d)\b/g, '0$1'), 'MM/DD/YYYY');
            }
          }

          const vr = p.validationRule.toLowerCase();

          if(vr === 'batesnumber' || vr === 'autogenerated') {
            p.value = vr === 'batesnumber' ? 'Bates Number' : 'Auto Generated';
          }
        }

        setProperties(props);
        setInitLoading(false);
      }
      
      populateProperties();
    }
  }, [workingFolder, selectedProfile])

  const handleProfileChange = (profileId) => {
    setProperties([]);
    setSelectedProfile(profileId)
  }

  const debouncedUpdateChildProperties = useCallback(
    _.debounce(async (props, id, profile) => {
      const updatedProps = await updateChildProperties(props, id, profile);
      setProperties(() => updatedProps);
    }, 300),
    []
  );

  const handlePropertyChanged = async (id, value) => {
    // copy the current properties array
    const currentProps = [...properties];
    const props = [...currentProps];
  
    const prop = _.find(props, (o) => o.id === id);
    if (!prop) {
      console.error('Property not found');
      return;
    }
  
    // update the selected property
    prop.open = !closePropDropdownOnSelect;
    if (prop.type.toLowerCase() === 'date') {
      prop.value = value ? dayjs(value, 'MM/DD/YYYY') : '';
    } else {
      prop.value = value;
    }

    if (prop.type.toLowerCase() === "text") {
      setProperties([...props]);
      debouncedUpdateChildProperties(props, id, selectedProfile);
    } else {
      const updatedProps = await updateChildProperties([...props], id, selectedProfile);
      setProperties([...updatedProps]);
    }
  };
  
  const closeDropdown = (id) => {
    setProperties(props => {
      _.find(props, o => o.id === id).open = false
      return [...props]
    });
  }

  const handleOpenChange = (open, id) => {
    setProperties(props => {
      _.find(props, o => o.id === id).open = open;
      return [...props]
    });
  }

  return(
    <Container>
      <ProfileContainer>
        <Select
          options={profileData}
          value={selectedProfile}
          style={{width: '100%'}}
          onChange={handleProfileChange}
          fieldNames={{
            label: 'id',
            value: 'id'
          }}
        />
      </ProfileContainer>
        <ListContainer>
          <List
            loading={initLoading}
            itemLayout="horizontal"
            dataSource={properties}
            renderItem={(p) => (
              <List.Item>
                <PropertyField backgroundColor={p.backColor}>
                  <span style={{float: 'left', fontSize: '12px'}}>{p?.name}</span>
                  {p.required && <span style={{float: 'right', fontSize: '12px'}}>*required</span>}
                  {p.type?.toLowerCase() === 'combo' &&
                    <Select
                      size="small"
                      mode="multiple"
                      open={p.open}
                      options={p.options}
                      value={p.value || []}
                      filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                      style={{width: '100%'}}
                      onBlur={() => closeDropdown(p.id)}
                      fieldNames={{label: 'name', value: 'name'}}
                      onChange={v => handlePropertyChanged(p.name, v)}
                      onDropdownVisibleChange={(open) => handleOpenChange(open, p.id)}
                    />
                  }      
                  {p.type?.toLowerCase() === 'text' &&
                    <Input
                      size="small"
                      value={p.value}
                      disabled={p.validationRule.toLowerCase() === 'autogenerated' || p.validationRule.toLowerCase() === 'batesnumber'}
                      onChange={e => handlePropertyChanged(p.name, e.target.value)}
                    />
                  }
                  {p.type?.toLowerCase() === 'date' &&
                    <DatePicker
                      size="small"
                      format="MM/DD/YYYY"
                      value={p.value}
                      style={{width: '100%'}}
                      onChange={e => handlePropertyChanged(p.name, e?.format('MM/DD/YYYY'))}
                    />
                  }
                </PropertyField>
              </List.Item>
            )}
          />
        </ListContainer>
    </Container>
  )
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  .ant-list-item  {
    padding: 0;
    color: rgb(0, 0, 0, 0.85);
  }
  .ant-list-item::before {
    color: rgb(0, 0, 0, 0.85);
    mix-blend-mode: difference;
    width: 200px;
    border: 1px solid rgb(0, 0, 0, 0.85);
  }
  .ant-list-item {
    border-bottom: none;
  }
  .ant-list-item:first-child {
    margin-top: 5px;
  }
`

const ProfileContainer = styled.div`
  .ant-select {
    z-index: 5;
    box-shadow: 0px 4px 10px -6px rgba(0,0,0,0.48);
  }
  .ant-select-selector {
    border: 1px solid #aaa !important;
  }
  .ant-select-selection-item {
    font-weight: 500;
  }
`

const ListContainer = styled.div`
  height: calc(100% - 35px);
  background-color: #fff;
  overflow-y: auto;
  padding-top: 5px;
`

const PropertyField = styled.div`
  width: 100%;
  padding: 0 5px 10px 5px;
  border-radius: 5px;
  background-color: ${p => p.backgroundColor ? `#${p.backgroundColor}` : '#fff' };
`

const mapStateToProps = state => {
  const { closePropDropdownOnSelect } = state.app;
  return { closePropDropdownOnSelect };
};

export default connect(mapStateToProps)(ProfileProperties);